// components/platform/PlatformJobsSection.jsx - Updated with working details
import React, { useState, useEffect } from 'react';
import { Sparkles, Code, Palette, Users, Briefcase, Award, ChevronRight, ChevronDown, Info } from 'lucide-react';
import { useJobs } from '../../context/JobsContext';
import { useAuth } from '../../context/AuthContext';
import JobCard from '../logistics/JobCard';

const PlatformJobsSection = ({ onJobSelect }) => {
  const { allJobs, getJobMatchScore } = useJobs();
  const { user } = useAuth();
  const [platformJobs, setPlatformJobs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState({});

  // Platform job categories with icons
  const platformCategories = {
    development: { icon: Code, color: 'blue' },
    content: { icon: Palette, color: 'purple' },
    support: { icon: Users, color: 'green' },
    business: { icon: Briefcase, color: 'orange' }
  };

  useEffect(() => {
    // Filter platform jobs from all jobs
    const filteredPlatformJobs = allJobs.filter(job => 
      job.isPlatformJob || job.postedBy === 'UniLedge Platform' || job.groupId === 'platform' || job.groupId === 'uniledge_platform'
    );
    setPlatformJobs(filteredPlatformJobs);
  }, [allJobs]);

  // Platform perks display
  const platformPerks = [
    'UniLedge Contributor Badge',
    '+0.2 Trust Score Bonus',
    'Early Access to Features',
    'Vote on Platform Decisions',
    'Direct Impact on Movement'
  ];

  const toggleJobDetails = (jobId) => {
    setExpandedJobs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200 p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
            <Sparkles className="text-blue-600" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Help Build UniLedge
            </h2>
            <p className="text-sm text-gray-600">
              Use our platform to build our platform - earn tokens while creating impact
            </p>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1"
        >
          {expanded ? 'Show Less' : 'Show More'}
          <ChevronRight 
            className={`transition-transform ${expanded ? 'rotate-90' : ''}`} 
            size={16} 
          />
        </button>
      </div>

      {/* Platform Message */}
      {!expanded && (
        <div className="bg-white/70 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            <strong>Prove the model works:</strong> UniLedge uses its own platform to hire 
            skilled community members for development, content creation, support, and growth. 
            No VC funding needed - just members building for members.
          </p>
        </div>
      )}

      {/* Expanded Content */}
      {expanded && (
        <>
          {/* Why Platform Jobs Matter */}
          <div className="bg-white/70 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Info size={16} className="text-blue-600" />
              Why Platform Jobs Matter
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Proof of Concept:</strong> We use what we build</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>No VC Needed:</strong> Pay in tokens that have real value</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Community Ownership:</strong> Contributors become stakeholders</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Quality Control:</strong> Builders who believe create better products</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Organic Growth:</strong> Every contributor becomes an evangelist</span>
              </li>
            </ul>
          </div>

          {/* Platform Perks */}
          <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">
              Platform Contributor Perks
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {platformPerks.map((perk, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <Award className="text-blue-600 flex-shrink-0" size={16} />
                  <span className="text-gray-700">{perk}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Job Categories */}
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">
              Open Platform Positions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(platformCategories).map(([key, cat]) => {
                const Icon = cat.icon;
                const jobCount = platformJobs.filter(j => j.category === key).length;
                
                return (
                  <div 
                    key={key}
                    className="bg-white rounded-lg p-3 border border-gray-200"
                  >
                    <Icon className={`text-${cat.color}-600 mb-2`} size={20} />
                    <div className="text-sm font-medium capitalize">{key}</div>
                    <div className="text-xs text-gray-500">{jobCount} positions</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Platform Jobs List */}
      <div className="space-y-3">
        {(expanded ? platformJobs : platformJobs.slice(0, 2)).map(job => {
          const matchResult = getJobMatchScore(job.id);
          const isExpanded = expandedJobs[job.id];
          
          return (
            <div key={job.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <span className="text-yellow-500">🌟</span>
                      {job.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {job.groupTitle} • Posted by {job.postedBy}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{job.tokenReward}</div>
                    <div className="text-xs text-gray-600">tokens</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                  <span>{job.estimatedHours} hours</span>
                  <span>•</span>
                  <span>{job.location}</span>
                  {job.schedule?.flexible && (
                    <>
                      <span>•</span>
                      <span className="text-blue-600">Flexible Schedule</span>
                    </>
                  )}
                </div>
                
                {/* Match Score */}
                {matchResult && (
                  <div className="mb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-full rounded-full ${
                            matchResult.qualified ? 'bg-green-500' : 
                            matchResult.matchScore >= 50 ? 'bg-yellow-500' : 'bg-gray-400'
                          }`}
                          style={{ width: `${matchResult.matchScore}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">{matchResult.matchScore}% match</span>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleJobDetails(job.id)}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center justify-center gap-2"
                  >
                    Details
                    <ChevronDown 
                      className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                      size={16} 
                    />
                  </button>
                  {matchResult?.qualified && (
                    <button
                      onClick={() => onJobSelect(job)}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      Apply Now
                    </button>
                  )}
                </div>
              </div>
              
              {/* Expandable Details Section */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-sm text-gray-700">{job.description}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Required Skills</h4>
                    <div className="flex flex-wrap gap-2">
                      {job.requiredSkills.map(skill => (
                        <span key={skill.id} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                          {skill.name} (Tier {skill.tier})
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {job.preferredSkills.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Preferred Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {job.preferredSkills.map(skill => (
                          <span key={skill.id} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                            {skill.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm text-blue-900">
                      <strong>Platform Contributor Benefits:</strong> Earn trust score bonus, 
                      get early access to new features, and have a voice in platform decisions.
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Show More Button if not expanded and more jobs exist */}
      {!expanded && platformJobs.length > 2 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setExpanded(true)}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            View {platformJobs.length - 2} more platform positions
          </button>
        </div>
      )}

      {/* Call to Action */}
      <div className="mt-4 p-4 bg-blue-600 text-white rounded-lg text-center">
        <p className="text-sm font-medium">
          "They organized to extract our wealth. We're organizing to keep it."
        </p>
        <p className="text-xs mt-1 text-blue-100">
          Every platform job completed strengthens the movement
        </p>
      </div>
    </div>
  );
};

export default PlatformJobsSection;